import React from 'react';
import RadioGroupField from './RadioGroupField';
import DateRangeField from './DateRangeField';
import { DatePicker, DateTimePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import { TextField, Typography, InputAdornment } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import Nested from './Nested';
import NestedExpandable from './NestedExpandable';
import RepeatableSection from './RepeatableSection';
import SimpleTable from './SimpleTable';
import ExpenseRows from './ExpenseRows';
import EstimateTable from './EstimateTable';
import ErrorBoundary from '../ErrorBoundary';
import FileUploadField from './FileUploadField';

// FieldRenderer: Renders a single field object.
// - loadFromAPI (boolean): DynamicForm will attempt to populate this field from fetched JSON when schema.api.fetch is configured.
// - Dropdown & Autocomplete mapping:
//     For object-based options you can specify optionLabelKey / optionValueKey.
//     The component DISPLAYs the labelKey value but STORES only the primitive from valueKey in formData,
//     keeping payloads clean for backend submission (e.g. user id instead of full object).
// - mapFields (object): optional per props.field. Keys = target field names in formData, values = source property names from the selected option object.
//     When user selects an option, each mapping is applied: props.formData[target] = option[sourceProp]. Useful to derive multiple backend fields from single selection.
// - Autocomplete field type supports:
//     options (static primitives or objects), dynamicOptions via loadFromApiRoute/loadFromApiField,
//     multiple, freeSolo, optionLabelKey / optionValueKey, disableClearable, placeholder, helperText.
//     loadFromApiField supports dot-paths (e.g. "data.items") per DynamicForm enhancement.
function FieldRenderer(props) {
  if (!props.field || !props.formData) return null;

  // Conditional visibility
  if (props.field.condition) {
    const { field: condField, value } = props.field.condition;
    // Only compare inequality; allow falsy values like 0, '' to be valid matches
    const condVal = condField?.includes?.('.')
      ? condField.split('.').reduce((acc, k) => (acc == null ? acc : acc[k]), props.formData)
      : props.formData[condField];
    if (condVal !== value) return null;
  }

  // Unified value applier (assumes setFormData is a standard React state setter)
  const applyValue = (name, value) => {
    props.setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Set value using dot-path (e.g., "poc.id") creating objects as needed
  const applyValueByPath = (path, value) => {
    if (!path || typeof path !== 'string' || !path.includes('.')) {
      applyValue(path, value);
      return;
    }
    props.setFormData((prev) => {
      const next = { ...prev };
      const keys = path.split('.');
      let cursor = next;
      for (let i = 0; i < keys.length - 1; i++) {
        const k = keys[i];
        const v = cursor[k];
        if (v == null || typeof v !== 'object' || Array.isArray(v)) {
          cursor[k] = {};
        } else {
          cursor[k] = { ...v };
        }
        cursor = cursor[k];
      }
      cursor[keys[keys.length - 1]] = value;
      return next;
    });
  };

  const markDirty = (name) => {
    props.setDirtyMap?.((prev) => ({ ...prev, [name]: true }));
  };

  const isEmptyByType = (f, val) => {
    if (val == null) return true;
    if (f.type === 'text' || f.type === 'conditionalText') return String(val).trim() === '';
    if (f.type === 'date') return !val;
    if (f.type === 'dateRange') return !val?.start || !val?.end;
    if (f.type === 'autocomplete' || f.type === 'dropdown') {
      if (f.multiple) return !Array.isArray(val) || val.length === 0;
      return val === '' || val == null;
    }
    if (f.type === 'radio') return String(val).trim() === '';
    if (f.type === 'nested') return false; // skip for now
    return false;
  };

  const isRequired = Boolean(props.field.required || props.field.requiredToSubmit);
  const isDirty = Boolean(props.dirtyMap?.[props.field.name]);
  const hasError = isRequired && isDirty && isEmptyByType(props.field, props.formData[props.field.name]);
  const schemaLabelHasStar = (props.field.label || '').includes('*');
  const showAsterisk = isRequired && !schemaLabelHasStar;
  const inputLabelProps = {
    ...(showAsterisk ? { required: true } : {}),
  };

  const handleChange = (e) => {
    if (!e || !e.target) return;
    const { name, value } = e.target;
    applyValueByPath(name, value);
    markDirty(name);
  };

  const updateDateRange = (name, key, value) => {
    // Support dot-path names
    applyValueByPath(`${name}.${key}`, value);
  };

  const renderHelperText = (helperText) => {
    if (!helperText) return null;

    return (
      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
        {helperText}
      </Typography>
    );
  };

  // Intentionally avoid overriding base MUI colors/styles so dark/light themes work correctly.

  const getByPath = (obj, path) => {
    if (!obj || !path) return undefined;
    if (typeof path !== 'string') return obj[path];
    return path.split('.').reduce((acc, key) => (acc == null ? acc : acc[key]), obj);
  };

  return (
    <ErrorBoundary>
      {(() => {
        switch (props.field.type) {
          // text handled below with MUI TextField

          // AUTOCOMPLETE (new): supports static props.field.options or props.dynamicOptions[props.field.name]
          // Field attributes:
          //  type: 'autocomplete'
          //  name: string (required)
          //  label: string
          //  options: array (fallback if no dynamic options fetched)
          //  loadFromApiRoute / loadFromApiField: same mechanism as dropdown to populate props.dynamicOptions
          //  multiple: boolean (enable multi-select)
          //  optionLabelKey: string (key for label when options are objects; defaults to 'label' or 'name')
          //  optionValueKey: string (key for value when options are objects; defaults to 'value' or 'id')
          //  freeSolo: boolean (allow values outside options)
          //  disableClearable: boolean
          //  placeholder: string
          case 'autocomplete': {
            const allOptionsRaw = (props.dynamicOptions || {})[props.field.name] || props.field.options || [];
            let allOptions = Array.isArray(allOptionsRaw) ? allOptionsRaw : [];
            const isObjects = allOptions.length > 0 && allOptions.some((o) => typeof o === 'object' && o !== null);
            const labelKey = props.field.optionLabelKey || (isObjects ? (allOptions[0]?.label ? 'label' : 'name') : undefined);
            const valueKey = props.field.optionValueKey || (isObjects ? (allOptions[0]?.value ? 'value' : 'id') : undefined);

            // Apply client-side filters if specified
            // Field config supports two patterns:
            // 1. Static value: filterBy: { field: 'workflowState', value: 'Approved', matchType: 'contains' }
            // 2. Dynamic from formData: filterBy: { field: 'status', formDataKey: 'selectedStatus', matchType: 'equals' }
            // 3. Array of filters: filterBy: [{ field: 'status', value: 'Active' }, { field: 'type', formDataKey: 'typeFilter' }]
            if (props.field.filterBy && isObjects && allOptions.length > 0) {
              const filters = Array.isArray(props.field.filterBy) ? props.field.filterBy : [props.field.filterBy];
              allOptions = allOptions.filter((option) => {
                return filters.every((filter) => {
                  // Get filter value from static value or formData
                  const filterValue = filter.value !== undefined 
                    ? filter.value 
                    : (filter.formDataKey ? props.formData?.[filter.formDataKey] : null);
                  
                  if (filterValue == null || filterValue === '') return true; // No filter value, include all
                  
                  const optionFieldValue = getByPath(option, filter.field);
                  if (optionFieldValue == null) return false;
                  
                  const matchType = filter.matchType || 'equals';
                  const optionStr = String(optionFieldValue).toLowerCase();
                  const filterStr = String(filterValue).toLowerCase();
                  
                  if (matchType === 'contains') {
                    return optionStr.includes(filterStr);
                  } else if (matchType === 'equals') {
                    return optionStr === filterStr;
                  } else if (matchType === 'startsWith') {
                    return optionStr.startsWith(filterStr);
                  } else if (matchType === 'endsWith') {
                    return optionStr.endsWith(filterStr);
                  }
                  return true;
                });
              });
            }

            const currentValue = props.field.name?.includes?.('.')
              ? props.field.name.split('.').reduce((acc, k) => (acc == null ? acc : acc[k]), props.formData)
              : props.formData[props.field.name];

            const resolveValue = () => {
              if (!isObjects) return currentValue || (props.field.multiple ? [] : '');
              if (props.field.multiple) {
                if (!Array.isArray(currentValue)) return [];
                return allOptions.filter((opt) =>
                  currentValue.some((cv) => {
                    if (typeof cv === 'object') return valueKey ? getByPath(cv, valueKey) === getByPath(opt, valueKey) : cv === opt;
                    return valueKey ? getByPath(opt, valueKey) === cv : false;
                  }),
                );
              }
              if (!currentValue) return null;
              if (typeof currentValue === 'object') return currentValue;
              return allOptions.find((opt) => (valueKey ? getByPath(opt, valueKey) === currentValue : false)) || null;
            };

            const toStorable = (val) => {
              if (props.field.multiple) {
                if (!Array.isArray(val)) return [];
                if (!isObjects) return val; // primitives already
                return val.map((v) => (valueKey ? getByPath(v, valueKey) : v));
              }
              if (!isObjects) return val ?? '';
              return valueKey ? getByPath(val, valueKey) : val;
            };

            return (
              <Autocomplete
                id={`field-${props.field.name}`}
                multiple={Boolean(props.field.multiple)}
                freeSolo={Boolean(props.field.freeSolo)}
                disableClearable={Boolean(props.field.disableClearable)}
                options={allOptions}
                value={resolveValue()}
                onChange={(_, newValue) => {
                  if (props.readOnly || props.field.readOnly) return;
                  applyValueByPath(props.field.name, toStorable(newValue));
                  markDirty(props.field.name);
                  if (props.field.mapFields && newValue && typeof newValue === 'object') {
                    Object.entries(props.field.mapFields).forEach(([targetField, sourceProp]) => {
                      const candidates = Array.isArray(sourceProp) ? sourceProp : [sourceProp];
                      let mappedVal = undefined;
                      for (const sp of candidates) {
                        const mv = getByPath(newValue, sp);
                        if (mv !== undefined) { mappedVal = mv; break; }
                      }
                      if (mappedVal !== undefined) {
                        applyValueByPath(targetField, mappedVal);
                        markDirty(targetField);
                      }
                    });
                  }
                }}
                readOnly={props.readOnly || props.field.readOnly}
                getOptionLabel={(option) => {
                  if (option == null) return '';
                  if (typeof option !== 'object') return String(option);
                  return labelKey ? String(getByPath(option, labelKey) ?? '') : String(option.value ?? option.id ?? '');
                }}
                // Use valueKey + index for React keys to handle duplicate IDs in data
                getOptionKey={(option, index) => {
                  if (option == null) return `opt-${index}`;
                  if (typeof option !== 'object') return `${option}-${index}`;
                  const key = valueKey ? String(getByPath(option, valueKey) ?? '') : String(option.id ?? option.value ?? '');
                  return `${key}-${index}`;
                }}
                // Ensure proper comparison for value equality
                isOptionEqualToValue={(option, value) => {
                  if (option == null || value == null) return option === value;
                  if (typeof option !== 'object' || typeof value !== 'object') return option === value;
                  if (valueKey) {
                    return getByPath(option, valueKey) === getByPath(value, valueKey);
                  }
                  return option === value;
                }}
                slotProps={{
                  popupIndicator: {
                    disabled: props.readOnly || props.field.readOnly,
                    sx: (props.readOnly || props.field.readOnly) ? { display: 'none' } : undefined
                  },
                  clearIndicator: {
                    disabled: props.readOnly || props.field.readOnly,
                    sx: (props.readOnly || props.field.readOnly) ? { display: 'none' } : undefined
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    id={`field-${props.field.name}`}
                    variant="outlined"
                    label={props.field.label}
                    name={props.field.name}
                    placeholder={props.field.placeholder}
                    helperText={props.field.helperText}
                    fullWidth
                    required={isRequired}
                    error={hasError}
                    InputLabelProps={inputLabelProps}
                    InputProps={{
                      ...params.InputProps,
                      readOnly: props.readOnly || props.field.readOnly,
                    }}
                  />
                )}
              />
            );
          }

          case 'radio': {
            return (
              <RadioGroupField
                name={props.field.name}
                label={props.field.label}
                value={(props.field?.name?.includes?.('.')
                  ? props.field.name.split('.').reduce((acc, k) => (acc == null ? acc : acc[k]), props.formData)
                  : props.formData[props.field.name]) || ''}
                onChange={(e) => { handleChange(e); markDirty(props.field.name); }}
                options={props.field.options}
                required={isRequired}
                error={hasError}
                helperText={props.field.helperText}
                disabled={props.readOnly}
              />
            );
          }

          case 'date': {
            const rawDateVal = props.field?.name?.includes?.('.')
              ? props.field.name.split('.').reduce((acc, k) => (acc == null ? acc : acc[k]), props.formData)
              : props.formData[props.field.name];
            // Values can arrive as a Dayjs (form state) or a plain string
            // (e.g. mapFields auto-fill from a fetched option). Coerce strings
            // to Dayjs so the picker renders them; coercion is idempotent.
            const dateVal = rawDateVal ? (dayjs.isDayjs(rawDateVal) ? rawDateVal : dayjs(rawDateVal)) : null;
            // minDate / maxDate may be a Dayjs or a (formData) => Dayjs
            // function so schemas can declare relative bounds (e.g. "today
            // or future" for Trip Start).
            const resolveBound = (b) => {
              if (b == null) return undefined;
              const v = typeof b === 'function' ? b(props.formData) : b;
              if (!v) return undefined;
              return dayjs.isDayjs(v) ? v : dayjs(v);
            };
            const minDate = resolveBound(props.field.minDate);
            const maxDate = resolveBound(props.field.maxDate);
            return (
              <DatePicker
                label={props.field.label}
                value={dateVal}
                minDate={minDate}
                maxDate={maxDate}
                onChange={(newValue) => { if (props.readOnly) return; applyValueByPath(props.field.name, newValue); markDirty(props.field.name); }}
                // For read-only fields: hide the calendar-icon button
                // (its own tab stop) AND fall back to the v6-style plain
                // <input> DOM structure so tabIndex={-1} on htmlInput is
                // actually honored. MUI X v8's accessible-field structure
                // uses span sections instead of an input and ignores
                // htmlInput.tabIndex. For editable date fields we use
                // the v8 accessible-field DOM so Tab walks through the
                // date sections (MM → DD → YYYY) and then the calendar
                // icon button before leaving the field.
                disableOpenPicker={Boolean(props.field.readOnly)}
                enableAccessibleFieldDOMStructure={!props.field.readOnly}
                slotProps={{
                  textField: {
                    id: `field-${props.field.name}`,
                    variant: 'outlined',
                    fullWidth: true,
                    name: props.field.name,
                    placeholder: props.field.placeholder,
                    helperText: props.field.helperText,
                    required: isRequired,
                    error: hasError,
                    InputLabelProps: inputLabelProps,
                    sx: props.field.readOnly
                      ? {
                          opacity: 0.6,
                          '& input, & .MuiInputBase-input': { fontWeight: 700 },
                        }
                      : undefined,
                    slotProps: {
                      input: { readOnly: Boolean(props.field.readOnly) || props.readOnly },
                      // Skip read-only date pickers in the tab order.
                      htmlInput: props.field.readOnly ? { tabIndex: -1 } : undefined,
                    },
                  },
                }}
              />
            );
          }

          case 'datetime':
            return (
              <DateTimePicker
                label={props.field.label}
                value={(props.field?.name?.includes?.('.')
                  ? props.field.name.split('.').reduce((acc, k) => (acc == null ? acc : acc[k]), props.formData)
                  : props.formData[props.field.name]) || null}
                onChange={(newValue) => { if (props.readOnly) return; applyValueByPath(props.field.name, newValue); markDirty(props.field.name); }}
                slotProps={{
                  textField: {
                    id: `field-${props.field.name}`,
                    variant: 'outlined',
                    fullWidth: true,
                    name: props.field.name,
                    placeholder: props.field.placeholder,
                    helperText: props.field.helperText,
                    required: isRequired,
                    error: hasError,
                    InputLabelProps: inputLabelProps,
                    slotProps: {
                      input: { readOnly: Boolean(props.field.readOnly) || props.readOnly },
                    },
                  },
                }}
              />
            );

          case 'dateRange':
            return (
              <>
                <DateRangeField
                  start={(props.field?.name?.includes?.('.')
                    ? props.field.name.split('.').reduce((acc, k) => (acc == null ? acc : acc[k]), props.formData)
                    : props.formData[props.field.name])?.start || null}
                  end={(props.field?.name?.includes?.('.')
                    ? props.field.name.split('.').reduce((acc, k) => (acc == null ? acc : acc[k]), props.formData)
                    : props.formData[props.field.name])?.end || null}
                  onChange={(key, value) => { if (props.readOnly) return; updateDateRange(props.field.name, key, value); }}
                  readOnly={props.readOnly}
                />
                {renderHelperText(props.field.helperText)}
              </>
            );

          case 'nested':
            return (
              <Nested
                field={props.field}
                formData={props.formData}
                setFormData={props.setFormData}
              />
            );

          case 'nestedexpandable':
            return (
              <NestedExpandable
                field={props.field}
                formData={props.formData}
                setFormData={props.setFormData}
              />
            );

          case 'repeatableSection':
            return (
              <RepeatableSection
                field={props.field}
                formData={props.formData}
                setFormData={props.setFormData}
                dynamicOptions={props.dynamicOptions}
                dirtyMap={props.dirtyMap}
                setDirtyMap={props.setDirtyMap}
                readOnly={props.readOnly}
              />
            );

          case 'simpleTable':
            return (
              <SimpleTable
                field={props.field}
                formData={props.formData}
                setFormData={props.setFormData}
                readOnly={props.readOnly}
              />
            );

          case 'expenseRows':
            return (
              <ExpenseRows
                field={props.field}
                formData={props.formData}
                setFormData={props.setFormData}
                readOnly={props.readOnly}
              />
            );

          case 'time':
            return (
              <TextField
                id={`field-${props.field.name}`}
                variant="outlined"
                type="time"
                label={props.field.label}
                name={props.field.name}
                value={(props.field?.name?.includes?.('.')
                  ? props.field.name.split('.').reduce((acc, k) => (acc == null ? acc : acc[k]), props.formData)
                  : props.formData[props.field.name]) || ''}
                onChange={handleChange}
                InputLabelProps={{ ...inputLabelProps, shrink: true }}
                fullWidth
                error={hasError}
                helperText={hasError ? 'This field is required' : renderHelperText(props.field.helperText)}
                disabled={props.readOnly || props.field.readonly}
                placeholder={props.field.placeholder}
                required={isRequired}
                slotProps={{
                  input: props.field.dollars ? { startAdornment: <InputAdornment position="start">$</InputAdornment> } : undefined,
                  htmlInput: {
                    id: `field-${props.field.name}`,
                    name: props.field.name,
                  }
                }}
              />
            );

          case 'text':
          case 'conditionalText': {
            const fieldId = `field-${props.field.name}`;
            const inputId = props.field.multiline ? `${fieldId}-textarea` : `${fieldId}-input`;
            // Bold + muted (opacity) value text for fields the user
            // can't edit (hardcoded defaults, system-generated
            // computations). Form-level readOnly (view mode) is
            // excluded so the entire form doesn't switch to muted/bold
            // when viewing.
            const fieldReadOnlySx = props.field.readOnly
              ? {
                  opacity: 0.6,
                  '& .MuiInputBase-input': { fontWeight: 700 },
                }
              : null;
            const baseSx = props.field.multiline ? { '& textarea': { resize: 'vertical' } } : null;
            return (
              <TextField
                id={inputId}
                variant="outlined"
                label={props.field.label}
                name={props.field.name}
                value={(props.field?.name?.includes?.('.')
                  ? props.field.name.split('.').reduce((acc, k) => (acc == null ? acc : acc[k]), props.formData)
                  : props.formData[props.field.name]) ?? ''}
                onChange={handleChange}
                placeholder={props.field.placeholder}
                fullWidth
                multiline={Boolean(props.field.multiline)}
                minRows={props.field.multiline ? (props.field.minRows ?? 3) : undefined}
                maxRows={props.field.multiline ? (props.field.maxRows ?? 10) : undefined}
                slotProps={{
                  input: {
                    readOnly: Boolean(props.field.readOnly) || props.readOnly,
                    ...(props.field.dollars ? { startAdornment: <InputAdornment position="start">$</InputAdornment> } : {}),
                  },
                  // Skip read-only / hardcoded fields in the keyboard tab
                  // order so Tab jumps straight to the next editable field.
                  htmlInput: props.field.readOnly ? { tabIndex: -1 } : undefined,
                }}
                helperText={props.field.helperText}
                sx={{ ...(baseSx || {}), ...(fieldReadOnlySx || {}) }}
                required={isRequired}
                error={hasError}
                InputLabelProps={{ ...inputLabelProps, htmlFor: inputId }}
              />
            );
          }

          case 'rowselect':
            return (
              <input
                type="radio"
                id={`field-${props.field.name}-${props.field.rowIndex}`}
                name={props.field.name}
                checked={(props.field?.name?.includes?.('.')
                  ? props.field.name.split('.').reduce((acc, k) => (acc == null ? acc : acc[k]), props.formData)
                  : props.formData[props.field.name]) === props.field.rowIndex}
                onChange={() => applyValueByPath(props.field.name, props.field.rowIndex)}
              />
            );

          case 'dropdown': {
            const optionsRaw = (props.dynamicOptions || {})[props.field.name] || props.field.options || [];
            let options = Array.isArray(optionsRaw) ? optionsRaw : [];
            const isObjects = options.length > 0 && options.some((o) => typeof o === 'object' && o !== null);
            const labelKey = props.field.optionLabelKey || (isObjects ? (options[0]?.label ? 'label' : 'name') : undefined);
            const valueKey = props.field.optionValueKey || (isObjects ? (options[0]?.value ? 'value' : 'id') : undefined);

            // Apply client-side filters if specified (parity with autocomplete).
            // Supports a single filter object or an array of filters. See the
            // autocomplete case above for the supported shapes.
            if (props.field.filterBy && isObjects && options.length > 0) {
              const filters = Array.isArray(props.field.filterBy) ? props.field.filterBy : [props.field.filterBy];
              options = options.filter((option) => {
                return filters.every((filter) => {
                  const filterValue = filter.value !== undefined
                    ? filter.value
                    : (filter.formDataKey ? props.formData?.[filter.formDataKey] : null);

                  if (filterValue == null || filterValue === '') return true; // No filter value, include all

                  const optionFieldValue = getByPath(option, filter.field);
                  if (optionFieldValue == null) return false;

                  const matchType = filter.matchType || 'equals';
                  const optionStr = String(optionFieldValue).toLowerCase();
                  const filterStr = String(filterValue).toLowerCase();

                  if (matchType === 'contains') return optionStr.includes(filterStr);
                  if (matchType === 'equals') return optionStr === filterStr;
                  if (matchType === 'startsWith') return optionStr.startsWith(filterStr);
                  if (matchType === 'endsWith') return optionStr.endsWith(filterStr);
                  return true;
                });
              });
            }

            // Current stored value is primitive (valueKey) if object options; else direct primitive/ string
            const stored = props.field?.name?.includes?.('.')
              ? props.field.name.split('.').reduce((acc, k) => (acc == null ? acc : acc[k]), props.formData)
              : props.formData[props.field.name];

            const resolveValue = () => {
              if (!isObjects) return stored ?? '';
              if (stored == null || stored === '') return null;
              // If already an object (legacy), pass through; else map primitive to matching object
              if (typeof stored === 'object') return stored;
              return options.find((opt) => (valueKey ? getByPath(opt, valueKey) === stored : false)) || null;
            };

            const toStorable = (val) => {
              if (!isObjects) return val ?? '';
              if (val == null) return '';
              return valueKey ? getByPath(val, valueKey) : val; // store primitive id/value
            };

            return (
              <>
                <Autocomplete
                  id={`field-${props.field.name}`}
                  options={options}
                  value={resolveValue()}
                  onChange={(_, newValue) => {
                    if (props.readOnly) return;
                    applyValueByPath(props.field.name, toStorable(newValue));
                    markDirty(props.field.name);
                    if (props.field.mapFields && newValue && typeof newValue === 'object') {
                      Object.entries(props.field.mapFields).forEach(([targetField, sourceProp]) => {
                        const candidates = Array.isArray(sourceProp) ? sourceProp : [sourceProp];
                        let mappedVal = undefined;
                        for (const sp of candidates) {
                          const mv = getByPath(newValue, sp);
                          if (mv !== undefined) { mappedVal = mv; break; }
                        }
                        if (mappedVal !== undefined) {
                          applyValueByPath(targetField, mappedVal);
                          markDirty(targetField);
                        }
                      });
                    }
                  }}
                  readOnly={props.readOnly}
                  getOptionLabel={(option) => {
                    if (option == null) return '';
                    if (!isObjects) return String(option);
                    if (typeof option !== 'object') return String(option);
                    return labelKey ? String(getByPath(option, labelKey) ?? '') : String(option.value ?? option.id ?? '');
                  }}
                  // Use valueKey + index for React keys to handle duplicate IDs in data
                  getOptionKey={(option, index) => {
                    if (option == null) return `opt-${index}`;
                    if (typeof option !== 'object') return `${option}-${index}`;
                    const key = valueKey ? String(getByPath(option, valueKey) ?? '') : String(option.id ?? option.value ?? '');
                    return `${key}-${index}`;
                  }}
                  // Ensure proper comparison for value equality
                  isOptionEqualToValue={(option, value) => {
                    if (option == null || value == null) return option === value;
                    if (typeof option !== 'object' || typeof value !== 'object') return option === value;
                    if (valueKey) {
                      return getByPath(option, valueKey) === getByPath(value, valueKey);
                    }
                    return option === value;
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      id={`field-${props.field.name}`}
                      variant="outlined"
                      label={props.field.label}
                      name={props.field.name}
                      placeholder={props.field.placeholder}
                      helperText={props.field.helperText}
                      fullWidth
                      required={isRequired}
                      error={hasError}
                      InputLabelProps={inputLabelProps}
                      slotProps={{ ...params.slotProps, input: { ...params.InputProps, readOnly: props.readOnly } }}
                    />
                  )}
                  freeSolo={Boolean(props.field.freeSolo)}
                />
              </>
            );
          }

          case 'fileUpload':
            return (
              <>
                <FileUploadField
                  label={props.field.label}
                  name={props.field.name}
                  objectType={props.field.objectType}
                  formSectionId={props.field.formSectionId}
                  value={props.formData[props.field.name]}
                  onFilesChange={(files) => applyValue(props.field.name, files)}
                  readOnly={props.readOnly}
                />
                {renderHelperText(props.field.helperText)}
              </>
            );

          case 'estimateTable':
            return (
              <EstimateTable
                field={props.field}
                formData={props.formData}
                setFormData={props.setFormData}
                dirtyMap={props.dirtyMap}
                setDirtyMap={props.setDirtyMap}
                readOnly={props.readOnly}
              />
            );

          default:
            return (
              <Typography color="error">
                Unsupported field type: {props.field.type}
              </Typography>
            );
        }
      })()}
    </ErrorBoundary>
  );
}

// Memoize FieldRenderer so unrelated changes don't force a re-render.
const areEqual = (prev, next) => {
  const name = prev.field?.name;
  // Compare current value by resolving possible dot-path
  const getVal = (obj, path) => {
    if (!path || typeof path !== 'string') return obj?.[path];
    return path.split('.').reduce((acc, k) => (acc == null ? acc : acc[k]), obj);
  };
  const sameField = prev.field === next.field;
  const sameValue = Object.is(getVal(prev.formData, name), getVal(next.formData, name));
  const sameOptions = prev.dynamicOptions?.[name] === next.dynamicOptions?.[name];
  const sameDirty = prev.dirtyMap?.[name] === next.dirtyMap?.[name];
  
  // For container-like fields (which render nested children), re-render when dynamicOptions map changes
  // so their children can receive updated option arrays.
  const isContainer = prev.field?.type === 'repeatableSection' || prev.field?.type === 'nestedexpandable' || prev.field?.type === 'simpleTable' || prev.field?.type === 'expenseRows' || prev.field?.type === 'nested' || prev.field?.type === 'columnLayout';
  if (isContainer && prev.dynamicOptions !== next.dynamicOptions) {
    return false;
  }

  // ExpenseRows renders the running Total Estimated Trip Cost footer
  // from formData.totalNTETripCost, which lives outside this field's
  // own value (formData.expenses). Without this check, memoization on
  // expenses alone would keep the footer stale when the parent's
  // subtotal effect updates totalNTETripCost without changing the
  // expenses array reference.
  if (prev.field?.type === 'expenseRows' && prev.formData?.totalNTETripCost !== next.formData?.totalNTETripCost) {
    return false;
  }

  // Check if conditional field dependency has changed
  let sameCondition = true;
  if (prev.field?.condition && next.field?.condition) {
    const condField = prev.field.condition.field;
    const prevCondVal = getVal(prev.formData, condField);
    const nextCondVal = getVal(next.formData, condField);
    sameCondition = Object.is(prevCondVal, nextCondVal);
  }
  
  return sameField && sameValue && sameOptions && sameDirty && sameCondition;
};

export default React.memo(FieldRenderer, areEqual);
